import React, { useState } from "react"
import { useRecordContext, useTranslate } from "react-admin"
import { Box } from "@mui/material"

import { UserRecord, getUserLanguages, UserLanguages } from "api/users"
import RoundedPanel, { Label } from "common/RoundedPanel"
import ActionButton from "components/ActionButton"
import { EditLanguagesModal } from "./profileModals/EditLanguagesModal"
import { handleEsc } from "common/closeOnEscape"

import "styles.scss"
import { Container } from "components/Status"

interface LanguageListProps {
  label: string
  languages: string[]
  backgroundColor: string
  translate: (key: string) => string
}

const LanguageList = ({ label, languages, backgroundColor, translate }: LanguageListProps) => {
  if (!languages || languages.length === 0) {
    return null
  }

  return (
    <>
      <Label noSelect>{label}: </Label>
      <Box display="flex" flexWrap="wrap" gap={1} marginBottom={2}>
        {languages.map((lang, index) => (
          <Container key={index} backgroundColor={backgroundColor} color="#043958">
            {translate(`dict.lang.${lang}`)}
          </Container>
        ))}
      </Box>
    </>
  )
}

const Languages = () => {
  const record = useRecordContext<UserRecord>()
  const translate = useTranslate()
  const [languages, setLanguages] = useState<UserLanguages | null>(null)
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)

  const fetchLanguages = () => {
    if (!record?.id) return

    getUserLanguages(record.id)
      .then((response) => {
        setLanguages({
          nativeLanguages: response.nativeLanguages.map((lang) => lang.toUpperCase()),
          mainLanguages: response.mainLanguages.map((lang) => lang.toUpperCase()),
          additionalLanguages: response.additionalLanguages.map((lang) => lang.toUpperCase())
        })
        setLoading(false)
      })
      .catch((error) => {
        console.warn("Failed to fetch languages data:", error)
        setLoading(false)
      })
  }

  React.useEffect(() => {
    fetchLanguages()
  }, [record?.id])

  const closeModal = () => {
    setOpen(false)
  }

  React.useEffect(handleEsc(closeModal), [])

  const handleSave = () => {
    fetchLanguages()
  }

  if (loading) {
    return (
      <Box className="panel">
        <RoundedPanel width="600px">
          <Label>Loading...</Label>
        </RoundedPanel>
      </Box>
    )
  }

  if (!languages) {
    return (
      <Box className="panel">
        <RoundedPanel width="600px">
          <Label>No language information available.</Label>
        </RoundedPanel>
      </Box>
    )
  }

  return (
    <>
      <EditLanguagesModal
        record={record}
        open={open}
        onClose={closeModal}
        initialLanguages={languages}
        onSave={handleSave}
      />
      <Box className="edit-tab">
        <Box className="on-left">
          <ActionButton
            onClick={() => {
              setOpen(true)
            }}
          >
            Edit
          </ActionButton>
        </Box>
      </Box>
      <Box className="panel">
        <RoundedPanel width="600px">
          <Label>Languages</Label>
          <hr />
          <LanguageList
            label="Native Languages"
            languages={languages.nativeLanguages || []}
            backgroundColor="#e3f2fd"
            translate={translate}
          />
          <LanguageList
            label="Main Languages"
            languages={languages.mainLanguages || []}
            backgroundColor="#f3e5f5"
            translate={translate}
          />
          <LanguageList
            label="Additional Languages"
            languages={languages.additionalLanguages || []}
            backgroundColor="#fff3e0"
            translate={translate}
          />
          {(!languages.nativeLanguages || languages.nativeLanguages.length === 0) &&
            (!languages.mainLanguages || languages.mainLanguages.length === 0) &&
            (!languages.additionalLanguages || languages.additionalLanguages.length === 0) && (
              <Label noSelect>No languages specified.</Label>
            )}
          <Box height={16} />
        </RoundedPanel>
      </Box>
    </>
  )
}

export default Languages
